import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { FormControl } from '@angular/forms';

import { MatDialog } from '@angular/material/dialog';
import { TooltipPosition } from '@angular/material/tooltip';

import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';

import { degrees, PDFDocument, rgb, StandardFonts } from 'pdf-lib';

declare const gtag: Function;

import { ConfirmActionDialogComponent } from '@app/components/shared/dialogs/confirm-action-dialog/confirm-action-dialog.component';

import { DownloadedItem, MaterialAction, User } from '@app/models';
import { AuthService, DownloadedItemService, MaterialActionService, MaterialHelperService, MaterialService, ReviewService, SharedUtilsService } from '@app/services';

@Component({
  selector: 'view-detailed-material',
  templateUrl: './view-detailed-material.component.html',
  styleUrl: './view-detailed-material.component.scss',
  standalone: false
})
export class ViewDetailedMaterialComponent implements OnInit, OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  currentUser: User = null;
  isAdmin = false;
  isOwned = false;
  itemToBeViewed: any = {};
  itemsCategories: any = [];
  itemsSubs: any = [];
  currentItemId: string;
  viewType: string;
  currentItemType: string;
  largeImageToShow: any;
  selectedImage: any;
  actionStore: any = [];

  showDownloadBtn: boolean = false;
  hasEditPermission: boolean = false;

  allRelated: any[] = null;
  itemsPerPage = 4;
  currentPage = 1;
  totalItemsCount = 0;
  defaultIcon = '../../assets/images/avatar1.png';

  // Review Stuff.
  reviewType = 'material';
  allReviews: any = [];
  fiveStarReviews: any = [];
  fourStarReviews: any = [];
  threeStarReviews: any = [];
  twoStarReviews: any = [];
  oneStarReviews: any = [];
  currentRatingTotal: number = 0;
  hasReviewed: boolean = false;
  shouldForceReview: boolean = false;
  totalReviewsToShow: number = 3; // number of comments to show at a time
  showMoreCount: number = 3; // number of new comments to show when view more is clicked

  positionOptions: TooltipPosition[] = ['after', 'before', 'above', 'below', 'left', 'right'];
  position = new FormControl(this.positionOptions[0]);
  position2 = new FormControl(this.positionOptions[1]);
  position3 = new FormControl(this.positionOptions[2]);

  constructor(
    private _router: Router,
    private _location: Location,
    private route: ActivatedRoute,
    private _authService: AuthService,
    private _downloadedItemService: DownloadedItemService,
    private _materialActionService: MaterialActionService,
    private _materialHelperService: MaterialHelperService,
    private _materialService: MaterialService,
    private _reviewService: ReviewService,
    private _sharedService: SharedUtilsService,
    public purchaseMaterialDialog: MatDialog,
    public flagContentDialog: MatDialog,
    public confirmActionDialog: MatDialog,
    public reviewDialog: MatDialog
  ) {
    this.route.params.pipe(takeUntil(this.ngUnsubscribe)).subscribe(params => {
      this.currentItemId = params.id;
    });
  }

  ngOnInit() {
    this.initMaterialDetails();
  }


  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }


  async initMaterialDetails() {
    this.currentUser = this._authService.getCurrentlyLoggedInUser();
    this.isAdmin = this._authService.checkForAdminPrivileges(this.currentUser);
    // console.log('Current user: ', this.currentUser);
    // console.log('isAdmin: ', this.isAdmin);

    this.getMaterialToView();
  }


  showMoreReviews() {
    // increment number of comments shown
    this.totalReviewsToShow += this.showMoreCount;

    // check if we are past the number of replies. set text to length since splice will ignore
    if (this.totalReviewsToShow > this.allReviews.length) this.totalReviewsToShow = this.allReviews.length;
  }


  showLessReviews(el: HTMLElement) {
    if (this.totalReviewsToShow > this.showMoreCount) {
      let remainder: number = this.totalReviewsToShow % this.showMoreCount;
      if (remainder === 0) this.totalReviewsToShow = Math.abs(this.totalReviewsToShow - this.showMoreCount);
      else this.totalReviewsToShow = Math.abs(this.totalReviewsToShow - remainder);
      el.scrollIntoView({ behavior: 'smooth' });
    }
  }


  updateReviewTotal() {
    let remainder: number = this.totalReviewsToShow % this.showMoreCount;
    if (remainder !== 0) this.totalReviewsToShow++;
  }


  clearReviewSection() {
    this.shouldForceReview = false;
    this.allReviews = [];
    this.fiveStarReviews = [];
    this.fourStarReviews = [];
    this.threeStarReviews = [];
    this.twoStarReviews = [];
    this.oneStarReviews = [];
  }


  async getMaterialToView() {
    const _foundItem = await lastValueFrom(this._materialService.getMediaById(this.currentItemId));
    if (_foundItem?._id) {
      this.itemToBeViewed = _foundItem;
      this.initializeMaterial();
    } else {
      this._sharedService.showErrorDialog('Material Not Found.', 6000);
    }
  }


  initializeMaterial() {
    this.checkForOwnership();
    // this.getMaterialReviews();
    this.getRelatedMaterial();

    if (this.itemToBeViewed.defaultImage) this.largeImageToShow = this.itemToBeViewed.defaultImage;
    if (this.itemToBeViewed.fileImageArray && this.itemToBeViewed.fileImageArray.length > 0) this.selectedImage = this.itemToBeViewed.fileImageArray[0];
  }


  async checkForOwnership() {
    if (this.currentUser) {
      const _ownershipInfo = await lastValueFrom(this._downloadedItemService.checkIfUserOwns(this.currentUser._id, this.itemToBeViewed._id));
      // console.log('_ownershipInfo: ', _ownershipInfo);

      if (_ownershipInfo != undefined) {
        const incomingMaterial = _ownershipInfo['material'] ?? null;

        if (incomingMaterial?._id === this.itemToBeViewed?._id) this.isOwned = true;
        else this.isOwned = false;
      }

      this.determineViewingType();
    } else {
      this.isOwned = false;
      this.determineViewingType();
    }
  }


  determineViewingType() {
    // Get User View Type
    const materialUserId: any = this.itemToBeViewed?.user?._id ?? this.itemToBeViewed?.user ?? null;

    if (this.isAdmin) this.viewType = 'admin';
    else if (materialUserId && this.currentUser?._id && this.currentUser._id === materialUserId) this.viewType = 'creator';
    else if (this.isOwned) this.viewType = 'owned';
    else this.viewType = 'viewed';
    // console.log('View Type: ', this.viewType);

    // Get User's Button Permissions
    if (this.viewType === 'admin' || this.viewType === 'creator') this.hasEditPermission = true;
    this.showDownloadBtn = true; // Right now anyone can download. If not logged in it will send to login.
  }


  async getMaterialReviews() {
    this.clearReviewSection();

    try {
      const _incomingReviews = await lastValueFrom(this._reviewService.getMaterialsReviews(this.currentItemId));
      this.allReviews = _incomingReviews?.length ? _incomingReviews : [];
    } catch (_err) {
      console.log('Error fetching material reviews:', _err);
      this.allReviews = [];
    }

    if (this.currentUser?._id) this.allReviews.map(_review => { if (_review.user && _review.user._id === this.currentUser._id) this.hasReviewed = true; });
    if (this.allReviews.length <= 3) this.totalReviewsToShow = this.allReviews.length;
    this.separateReviewsByRating();
  }


  separateReviewsByRating() {
    if (this.allReviews.length > 0) {
      this.allReviews.forEach(review => {
        if (review.rating === 5) this.fiveStarReviews.push(review);
        else if (review.rating === 4) this.fourStarReviews.push(review);
        else if (review.rating === 3) this.threeStarReviews.push(review);
        else if (review.rating === 2) this.twoStarReviews.push(review);
        else if (review.rating === 1) this.oneStarReviews.push(review);
      });
    }
  }


  changeLargeImage(imagePassed: any) {
    this.selectedImage = imagePassed;
    this.largeImageToShow = imagePassed;
  }


  goToUserProfile() {
    // if (this.currentUser?._id) {
    //   const materialUserId: any = this.itemToBeViewed?.user?._id ?? this.itemToBeViewed?.user ?? null;

    //   if (materialUserId === this.currentUser._id) this._router.navigate(['/material/store-profile']);
    //   else {
    //     if (this.itemToBeViewed.user._id) this._router.navigate(['/material/store-profile/' + this.itemToBeViewed.user._id]);
    //     else this._sharedService.showErrorDialog('This material has no current user by that name. User Not Found', 6000);
    //   }
    // } else {
    //   this._sharedService.showErrorDialog('You must be logged in to view a users profile. Access Denied', 6000);
    // }
  }


  interactWithMaterial() {
    // Set material to interact with
    // this._materialHelperService.passInteractiveMaterial(this.itemToBeViewed);
    // this._router.navigate(['/interact/' + this.itemToBeViewed._id]);
  }


  async checkDownloadAccess() {
    if (this.currentUser) {
      this.performDownload();
    } else {
      this.forceLogin('You must be signed in to download a material!');
    }
  }


  async performDownload() {
    const a = document.createElement('a');
    let downloadUrl: string = '';

    if (this.itemToBeViewed.fileType === 'PDF' || this.itemToBeViewed.fileType === 'Video') {
      downloadUrl = this.itemToBeViewed?.filePath ?? '';
    } else {
      downloadUrl = this.itemToBeViewed?.defaultImage ?? '';
    }

    // Exit early if no URL for download.
    if (!downloadUrl?.length) {
      this.showSnackBarMessage('Path to material file not found!', 'Unsuccessful');
      return;
    }

    // Set the defaults for the download.
    let href = downloadUrl;
    let download = downloadUrl.slice(downloadUrl.lastIndexOf('/') + 1);

    // If this is a pdf and not admin or creator, then append the copyright to each page.
    if (this.itemToBeViewed?.fileType === 'PDF' && (this.viewType !== 'creator' && !this.isAdmin)) {
      const existingPdfBytes = await fetch(downloadUrl).then(res => res.arrayBuffer()); // Fetch an existing PDF document
      const pdfDoc = await PDFDocument.load(existingPdfBytes); // Load a PDFDocument from the existing PDF bytes
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica); // Embed the Helvetica font
      const pages = pdfDoc.getPages(); // Get pages of the document
      const thisYear = new Date().getFullYear(); // Getting current year to add to copyright string
      const copyrightText = `\u00A9 ${thisYear} eduhealthcare.com`; // Creating copyright string

      pages.forEach(_page => {
        let { width, height } = _page.getSize(); // Get the width and height of the first page

        // Draw text on page
        // The commonly used RGB color values in the range 0-255 must be divided by 255 in order to scale them to the range 0-1 as required by PDFlib.
        _page.drawText(copyrightText, {
          x: 10,
          y: 10,
          size: 8,
          font: helveticaFont,
          color: rgb(0.35, 0.38, 0.412),
          rotate: degrees(0),
        })
      });

      const pdfBytes = await pdfDoc.save(); // Serialize the PDFDocument to bytes (a Uint8Array)
      const bytes = new Uint8Array(pdfBytes); // pass your byte response to this constructor
      const blob = new Blob([bytes], { type: "application/pdf" });// change resultByte to bytes

      href = window.URL.createObjectURL(blob);
    }

    // Perform the download with built element and files link.
    a.href = href;
    a.download = download;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    try {
      gtag('event', 'material_download', {
        event_category: 'Material Download',
        event_label: this.itemToBeViewed?.title || '',
        material_id: this.itemToBeViewed?._id || '',
        file_type: this.itemToBeViewed?.fileType || ''
      });
    } catch (_e) {}

    // Create a download item for the current user.
    if (this.viewType !== 'creator') this.createDownloadItem();
  }


  async createDownloadItem() {
    // Creating Download item here
    const materialDownload = new DownloadedItem();
    materialDownload.user = this.currentUser?._id;
    materialDownload.material = this.itemToBeViewed?._id;

    // Only creates one if not one for this user and material already.
    const _savedDownload = await lastValueFrom(this._downloadedItemService.create(materialDownload));
    if (_savedDownload === 'success') {
      this.showSnackBarMessage('This material has been added to your download history', 'Successful');
      this.createMaterialAction(this.itemToBeViewed);

      if (this.viewType === 'viewed') {
        this.isOwned = true;
        this.viewType = 'owned';
      }
    } else {
      this.createMaterialAction(this.itemToBeViewed);
    }
  }


  async createMaterialAction(item: any) {
    if (item?.user && (this.viewType !== 'creator' && !this.isAdmin)) {
      const materialAction = new MaterialAction();
      materialAction.actionType = 'downloaded';
      materialAction.owner = item?.user?._id ?? item?.user;
      materialAction.material = item._id;
      if (this.currentUser._id) materialAction.visitor = this.currentUser._id;

      const _resMaterialAction = await lastValueFrom(this._materialActionService.save(materialAction));
      if (_resMaterialAction) {
        this.actionStore.push(_resMaterialAction);
        // this.checkForUserReview();
      }
    }

    // else {
    //   this.checkForUserReview();
    // }
  }


  async checkForUserReview() {
    if (this.viewType !== 'creator' && !this.isAdmin) {
      const actionToCheck = {
        visitor: this.currentUser._id,
        material: this.itemToBeViewed._id
      };

      const _savedDownload = await lastValueFrom(this._materialActionService.checkForUserActions(actionToCheck));
      if (_savedDownload >= 2 && !this.hasReviewed) {
        this.shouldForceReview = true;
        this.leaveReview();
      }
    }
  }


  sortArrayByRating(rating: number) {
    for (let i = 0; i < this.allReviews.length; i++) {
      if (this.allReviews[i].rating === rating) {
        const a = this.allReviews.splice(i, 1);   // removes the item
        this.allReviews.unshift(a[0]);         // adds it back to the beginning
      }
    }
  }


  changeThePage(pageNo) {
    this.currentPage = pageNo;
    this.getRelatedMaterial();
  }


  leaveReview() {
    if (this.currentUser) {
      if (this.shouldForceReview) this.confirmShareReview();
      else this.showReviewDialog();
    } else {
      this.shouldForceReview = false;
      this.forceLogin('You must be signed in to leave a review!');
    }
  }


  async confirmShareReview() {
    const dialogRef = this.confirmActionDialog.open(ConfirmActionDialogComponent, { width: '80%', maxWidth: '700px' });
    const instance = dialogRef.componentInstance;
    instance.title = 'Please Share With Others';
    instance.textToDisplay = 'You have downloaded this more than once. Would you like to share what you think about this material with others?';

    const _userResponse = await lastValueFrom(dialogRef.afterClosed());
    this.shouldForceReview = false;
    if (_userResponse && _userResponse === true) this.showReviewDialog();
  }


  async showReviewDialog() {
    // const dialogRef = this.reviewDialog.open(AddReviewDialogComponent, { width: '80%', maxWidth: '700px' });
    // const instance = dialogRef.componentInstance;
    // instance.passedTitle = "Material";
    // instance.passedMaterial = this.itemToBeViewed;
    // instance.forceTriggered = false;

    // const _userResponse = await lastValueFrom(dialogRef.afterClosed());
    // this.shouldForceReview = false;
    // if (_userResponse && _userResponse.confirmationResult === true) this.saveReview(_userResponse.valueTOPassBack);
  }


  async saveReview(newReview) {
    const _savedReview = await lastValueFrom(this._reviewService.createReview(newReview));
    if (_savedReview) {
      this._sharedService.showNotification('Your review has been added. Review Created');
      this.hasReviewed = true;
      this.updateReviewTotal();
      this.getUpdatedMaterial();
    } else {
      this._sharedService.showErrorDialog('There was a problem creating your review. Review Error', 6000);
    }
  }


  async getUpdatedMaterial() {
    const _materialItem = await lastValueFrom(this._materialService.getMediaById(this.itemToBeViewed._id));
    if (_materialItem) {
      this.itemToBeViewed = _materialItem;
      // this.getMaterialReviews();
    }
  }


  async getRelatedMaterial() {
    let relatedParams;
    const catsArrayToPass = [];
    const subsArrayToPass = [];

    this.itemToBeViewed.categories.forEach(element => {
      if (element.subCategory && element.subCategory !== null) subsArrayToPass.push(element.subCategory);
      else {
        if (element.name && element.name !== null) catsArrayToPass.push(element.name);
      }
    });

    if (subsArrayToPass && subsArrayToPass.length) {
      relatedParams = {
        categories: catsArrayToPass,
        subCategories: subsArrayToPass,
        deleted: false
      };
    } else {
      relatedParams = {
        categories: catsArrayToPass,
        deleted: false
      };
    }

    let _queryResp: any = null;

    try {
      _queryResp = await lastValueFrom(this._materialService.getRelatedMaterials(relatedParams, this.currentPage, this.itemsPerPage));
    } catch (_err) {
      console.log('Error fetching related material: ', _err);
      _queryResp = null;
    }

    this.allRelated = (_queryResp?.message?.length) ? _queryResp?.message : [];
    this.totalItemsCount = _queryResp?.totalItemCount ?? 0;
  }


  newMaterialIdRecieved(event) {
    this.itemToBeViewed = event;
    this.currentItemId = event._id;
    this.hasReviewed = false;

    this.initializeMaterial();
    this.topFunction();
  }


  editMaterial() {
    this._materialService.setMaterialToEdit(this.itemToBeViewed);

    // Determine base path for edit.
    const isAdminRoute = this._router?.url?.startsWith('/admin');
    const basePath = isAdminRoute ? '/admin/materials' : '/materials';

    this._router.navigate([`${basePath}/edit-material/${this.itemToBeViewed?._id}`]);
  }


  async confirmationDelete() {
    const dialogRef = this.confirmActionDialog.open(ConfirmActionDialogComponent, { width: '80%', maxWidth: '700px' });

    const instance = dialogRef.componentInstance;
    instance.title = 'Delete Material';
    instance.textToDisplay = 'Are you sure you want to delete this material?';

    instance.items = [{_id: this.itemToBeViewed?._id, name: this.itemToBeViewed.title}];

    instance.buttonsInfo = {
      confirmText: 'Delete',
      confirmBtnColor: 'warn',
      denyText: 'Cancel',
      denyBtnColor: ''
    };

    const _confirmationResult = await lastValueFrom(dialogRef.afterClosed());
    if (_confirmationResult && _confirmationResult === true) this.deleteMaterial();
  }


  async deleteMaterial() {
    this.itemToBeViewed.deleted = true;

    try {
      const _updatedMediaItem = await lastValueFrom(this._materialService.update(this.itemToBeViewed));
      if (_updatedMediaItem && _updatedMediaItem?.deleted === true) {
        this.goBack();
        setTimeout(() => { this._sharedService.showNotification('This material has been marked for deletion. Material Removed'); }, 500);
      } else {
        this._sharedService.showErrorDialog('There was a problem removing this material. Material Error', 6000);
      }
    } catch (_err) {
      console.log('Error deleting material: ', _err);
    }
  }


  goBack(): void {
    this._location.back();
  }


  showSnackBarMessage(_message, _status) {
    if (_status === 'Successful') this._sharedService.showNotification(`${_message}. ${_status}`);
    else this._sharedService.showErrorDialog(`${_message}. ${_status}`, 6000);
  }


  forceLogin(_message?: string) {
    if (_message?.length) this.showSnackBarMessage(_message, 'Unsuccessful');

    // Set redirect to current material being viewed. so user can return here after login.
    const currentUrl = this._router?.url;
    // console.log('Redirect Url: ', currentUrl);

    this._authService.redirectUrl = currentUrl;
    this._router.navigate(['/login']);
  }


  topFunction() {
    document.body.scrollTop = 0; // For Safari
    document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
  }
}